<template>
  <div :class="['app-field', attrs.class]" :style="attrs.style">
    <AppLabel v-if="label" :for="inputId" :required="isRequired">{{ label }}</AppLabel>

    <ElInput v-bind="inputAttrs" :id="inputId" class="app-input" :validate-event="validateEvent">
      <template v-for="(_, name) in $slots" #[name]="slotProps">
        <slot :name="name" v-bind="slotProps || {}" />
      </template>
    </ElInput>
  </div>
</template>

<script setup>
import { computed, useAttrs, useId } from "vue";
import { ElInput } from "element-plus";
import AppLabel from "@/components/ui/AppLabel.vue";

defineOptions({ inheritAttrs: false });

defineProps({
  label: {
    type: String,
    default: "",
  },
  // Off by default: multi-step forms validate explicitly per step, and validating on
  // every keystroke would flag totals (e.g. surface percentages) while they're being typed.
  validateEvent: {
    type: Boolean,
    default: false,
  },
});

const attrs = useAttrs();
const generatedId = useId();

const inputId = computed(() => attrs.id || generatedId);
const isRequired = computed(() => attrs.required !== undefined && attrs.required !== false);

// class and style belong on the wrapper; everything else goes to the Element Plus control
const inputAttrs = computed(() =>
  Object.fromEntries(Object.entries(attrs).filter(([key]) => key !== "class" && key !== "style"))
);
</script>

<style lang="scss">
.el-input.app-input,
.el-textarea.app-input {
  --el-input-border-color: #{$color-border-strong};
  --el-input-hover-border-color: #{$color-grey-10};
  --el-input-focus-border-color: #{$color-primary};
  --el-input-border-radius: 8px;
  --el-input-text-color: #{$color-ink};
  --el-input-placeholder-color: #{$color-grey-10};
  font-size: 15px;
}

.el-input.app-input {
  --el-input-height: 42px;

  .el-input__wrapper {
    padding: 0 14px;
  }

  .el-input__wrapper.is-focus {
    box-shadow:
      0 0 0 1px $color-primary inset,
      0 0 0 3px rgba($color-primary, 0.15);
  }
}

.el-textarea.app-input .el-textarea__inner {
  border-radius: 8px;
  padding: 10px 14px;

  &:focus {
    box-shadow:
      0 0 0 1px $color-primary inset,
      0 0 0 3px rgba($color-primary, 0.15);
  }
}
</style>
